// Location field — opens the map picker, stays typeable once it is closed

import { useRef } from 'react';
import Input from './Input';
import LocationPicker from './LocationPicker';
import { useModal } from './ModalProvider';
import './InputLocation.scss';

export default function InputLocation({
  value,
  onChange,
  onBlur,
  name = 'location',
  placeholder = 'Location',
  level = 'address',
  error,
  className,
  disabled,
}) {
  const { openModal } = useModal();
  const inputRef = useRef(null);
  const skipFocus = useRef(false);
  // A click that also moves the focus fires both triggers — a second openModal
  // would strand the first one's promise, and the caret it brings back with it
  const openRef = useRef(false);

  const open = async () => {
    if (disabled || openRef.current) return;
    openRef.current = true;
    // While the picker is up, the field behind it takes no keystrokes
    inputRef.current?.blur();
    try {
      await openModal({
        header: level === 'city' ? 'Select city' : 'Select location',
        body: <LocationPicker initial={value} level={level} onPick={onChange} />,
      });
    } finally {
      openRef.current = false;
    }
    const input = inputRef.current;
    const active = document.activeElement;
    // The dismissing click may have landed on another field — stealing its
    // caret would misdirect keystrokes. The modal's own buttons are leaving
    const stolen =
      !!active && active !== document.body && !active.closest('.info-modal');
    if (!input || active === input || stolen) return;
    // The caret comes back, so the value can still be typed by hand
    skipFocus.current = true;
    input.focus();
    // A mobile browser may refuse focus() outside a user gesture, and then no
    // event ever comes to lower the flag — it would eat the next entry instead
    if (document.activeElement !== input) skipFocus.current = false;
  };

  return (
    <div className={'input-location' + (className ? ' ' + className : '')}>
      <Input
        name={name}
        placeholder={placeholder}
        value={value}
        error={error}
        disabled={disabled}
        inputRef={inputRef}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onFocus={() => {
          if (skipFocus.current) {
            skipFocus.current = false;
            return;
          }
          open();
        }}
        // A focused field gets no second focus event, so the repeat tap needs
        // this. Never pointerdown: GetClickOutside eats that same gesture
        onClick={open}
        onClear={value && !disabled ? () => onChange('') : undefined}
      />
      <span className='fa-location' onClick={open} />
    </div>
  );
}
